/**
 * @jsx jsx
 */
import { type ReactNode } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import Link from '@atlaskit/link';
import { FadeIn } from '@atlaskit/motion';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ConvoAIErrorMessage, type ConvoAIStreamError } from '@atlassian/ai-agent-streaming';
import { type QuickSummaryErrorResponseMessage } from '@atlassian/convo-ai-api';

import { i18nForPage } from './messages';

export const TROUBLE_SHOOTING_DOCS_URL =
	'https://support.atlassian.com/organization-administration/docs/troubleshooting-for-atlassian-intelligence/';
export const USAGE_LIMITS_DOCS_URL =
	'https://support.atlassian.com/organization-administration/docs/excessive-use-in-atlassian-intelligence/';
export const ACCEPTABLE_USE_POLICY_URL = 'https://www.atlassian.com/legal/acceptable-use-policy';

const QuickSummaryErrorBodyStyles = css({
	color: token('color.text.subtlest'),
	font: token('font.body'),
	overflowY: 'auto',
	overflowWrap: 'break-word',
	whiteSpace: 'normal',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'::-webkit-scrollbar': {
		width: token('space.100'),
		background: 'transparent',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'::-webkit-scrollbar-thumb': {
		borderRadius: token('space.100'),
		backgroundColor: token('color.background.neutral'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
});

interface AIStreamErrorProps {
	error: ConvoAIStreamError<QuickSummaryErrorResponseMessage['message_template']>;
}

export const AIStreamError = ({ error }: AIStreamErrorProps) => {
	const intl = useIntl();

	let errorMessage: ReactNode;

	switch (error.message.message_template) {
		case ConvoAIErrorMessage.ANSWER_UNAVAILABLE_FOR_CONTENT:
			errorMessage = intl.formatMessage(i18nForPage.tooMuchContent);
			break;
		case ConvoAIErrorMessage.ACCEPTABLE_USE_VIOLATIONS:
			errorMessage = intl.formatMessage(i18nForPage.aupViolation, {
				a: (chunks: React.ReactNode[]) => (
					<Link href={ACCEPTABLE_USE_POLICY_URL} target="_blank">
						{chunks}
					</Link>
				),
			});
			break;
		case ConvoAIErrorMessage.RATE_LIMIT:
			errorMessage = intl.formatMessage(i18nForPage.rateLimit, {
				a: (chunks: React.ReactNode[]) => (
					<Link href={USAGE_LIMITS_DOCS_URL} target="_blank">
						{chunks}
					</Link>
				),
			});
			break;
		default:
			errorMessage = intl.formatMessage(i18nForPage.generalError, {
				a: (chunks: React.ReactNode[]) => (
					<Link href={TROUBLE_SHOOTING_DOCS_URL} target="_blank">
						{chunks}
					</Link>
				),
			});
	}

	return (
		<FadeIn entranceDirection="top">
			{(props) => (
				<Box {...props} padding="space.250">
					<Stack space="space.250">
						<div css={QuickSummaryErrorBodyStyles}>
							<Text testId="qs-summary-error" as="p">
								{errorMessage}
							</Text>
						</div>
					</Stack>
				</Box>
			)}
		</FadeIn>
	);
};
